import fs from 'node:fs';
import { isMainThread } from 'node:worker_threads';
import { ensureUnderCwd } from '../ensure-under-cwd.js';
import {
  pathStaysUnderParent,
  resolveCacheKey,
  storeResolveCacheEntry,
} from './resolve-file-cache.js';

export type ResolveFileForReadResult = { path: string; size: number } | { error: string };

export function resolveFileForReadImplSync(
  relativePath: string,
  sessionParentPath: string,
): ResolveFileForReadResult {
  const cacheKey = resolveCacheKey(sessionParentPath, relativePath);

  const resolved = ensureUnderCwd(relativePath, sessionParentPath);
  if (!resolved) return { error: 'Path is outside working directory' };

  let real: string;
  let size: number;
  let mtimeMs: number;
  try {
    const lstat = fs.lstatSync(resolved);
    if (lstat.isSymbolicLink()) {
      real = fs.realpathSync(resolved);
      if (!pathStaysUnderParent(real, sessionParentPath)) {
        return { error: 'Path is outside working directory' };
      }
      const stat = fs.statSync(real);
      if (!stat.isFile()) return { error: 'Not a file' };
      size = stat.size;
      mtimeMs = stat.mtimeMs;
    } else if (lstat.isFile()) {
      real = resolved;
      size = lstat.size;
      mtimeMs = lstat.mtimeMs;
    } else {
      return { error: 'Not a file' };
    }
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }

  const entry = storeResolveCacheEntry(cacheKey, { path: real, size, mtimeMs });
  return { path: entry.path, size: entry.size };
}

export async function resolveFileForReadImpl(
  relativePath: string,
  sessionParentPath: string,
): Promise<ResolveFileForReadResult> {
  if (isMainThread) {
    return resolveFileForReadImplSync(relativePath, sessionParentPath);
  }

  const cacheKey = resolveCacheKey(sessionParentPath, relativePath);

  const resolved = ensureUnderCwd(relativePath, sessionParentPath);
  if (!resolved) return { error: 'Path is outside working directory' };

  let real: string;
  let size: number;
  let mtimeMs: number;
  try {
    const lstat = await fs.promises.lstat(resolved);
    if (lstat.isSymbolicLink()) {
      real = await fs.promises.realpath(resolved);
      if (!pathStaysUnderParent(real, sessionParentPath)) {
        return { error: 'Path is outside working directory' };
      }
      const stat = await fs.promises.stat(real);
      if (!stat.isFile()) return { error: 'Not a file' };
      size = stat.size;
      mtimeMs = stat.mtimeMs;
    } else if (lstat.isFile()) {
      real = resolved;
      size = lstat.size;
      mtimeMs = lstat.mtimeMs;
    } else {
      return { error: 'Not a file' };
    }
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }

  const entry = storeResolveCacheEntry(cacheKey, { path: real, size, mtimeMs });
  return { path: entry.path, size: entry.size };
}
